// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon, { ICONS } from '../../icons';
import { useLocalization, type Language } from '../../../../config/localization';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

interface Collection {
  id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_private: boolean;
  can_edit: boolean;
  total_images?: number;
}

interface CollectionSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedCollectionIds: number[], initialCollectionIds: number[]) => Promise<void>;
  collections: Collection[];
  selectedCollectionIds?: number[];
  selectedLanguage: Language;
  generationId?: number;
}

const CollectionSelectModal: React.FC<CollectionSelectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  collections,
  selectedCollectionIds = [],
  selectedLanguage,
  generationId
}) => {
  useEscapeKey(onClose, isOpen);
  const t = useLocalization(selectedLanguage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>(selectedCollectionIds);

  // Reset local selection only when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedIds(selectedCollectionIds);
      setSearchQuery('');
      setError(null);
    }
  }, [isOpen]);

  // Filter collections based on search query
  const filteredCollections = collections.filter(collection =>
    collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (collection.description && collection.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleToggleCollection = (collectionId: number) => {
    setLocalSelectedIds(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId);
      } else {
        return [...prev, collectionId];
      }
    });
  };

  const handleSelectAll = () => {
    const allIds = filteredCollections.map(c => c.id);
    const allSelected = allIds.every(id => localSelectedIds.includes(id));
    
    if (allSelected) {
      // Remove all filtered collections from selection
      setLocalSelectedIds(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      // Add all filtered collections to selection
      setLocalSelectedIds(prev => {
        const newIds = allIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  const handleSubmit = async () => {
    if (localSelectedIds.length === 0) {
      setError(t.collections?.messages?.selectAtLeastOne || 'Select at least one collection');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(localSelectedIds, selectedCollectionIds);
    } catch (error: any) {
      setError(error.message || t.collections?.messages?.updateError || 'Error updating collections');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedCount = localSelectedIds.length;
  const hasChanges = JSON.stringify(localSelectedIds.sort()) !== JSON.stringify(selectedCollectionIds.sort());

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Icon name={ICONS.FOLDER} size={24} className="text-purple-400" />
            <div>
              <h2 className="text-xl font-semibold text-white">
                {t.collections?.modal?.selectTitle || 'Выберите коллекции'}
              </h2>
              {selectedCount > 0 && (
                <div className="text-sm text-purple-200">
                  Выбрано: {selectedCount}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            disabled={loading}
          >
            <Icon name={ICONS.X} size={20} />
          </button>
        </div>

        {/* Search and filters */}
        <div className="p-4 border-b border-gray-700">
          {/* Search input */}
          <div className="relative mb-3">
            <Icon name={ICONS.SEARCH} size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск коллекций..."
              className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-4 pr-10 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              disabled={loading}
            />
          </div>

          {/* Select all button */}
          {filteredCollections.length > 1 && (
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-sm text-purple-300 hover:text-purple-200 transition-colors"
              disabled={loading}
            >
              {filteredCollections.every(c => localSelectedIds.includes(c.id))
                ? 'Снять выделение со всех'
                : 'Выбрать все'
              }
            </button>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-4 mt-4 bg-red-900/30 border border-red-600/50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Icon name={ICONS.ERROR} size={16} className="text-red-400 flex-shrink-0" />
              <span className="text-red-200 text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Collections list */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredCollections.length === 0 ? (
            <div className="text-center py-8">
              <Icon name={ICONS.SEARCH} size={48} className="mx-auto mb-3 text-gray-500" />
              <p className="text-gray-400">
                {searchQuery ? 'Коллекции не найдены' : t.collections?.noCollections || 'Коллекции не найдены'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCollections.map((collection) => (
                <div
                  key={collection.id}
                  className={`border rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                    localSelectedIds.includes(collection.id)
                      ? 'bg-purple-900/30 border-purple-500/50'
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600 hover:bg-gray-800/70'
                  }`}
                  onClick={() => handleToggleCollection(collection.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      localSelectedIds.includes(collection.id)
                        ? 'bg-purple-600 border-purple-600'
                        : 'border-gray-500'
                    }`}>
                      {localSelectedIds.includes(collection.id) && (
                        <Icon name={ICONS.CHECK} size={12} className="text-white" />
                      )}
                    </div>

                    {/* Collection info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon 
                          name={collection.is_private ? ICONS.LOCK : ICONS.FOLDER} 
                          size={16} 
                          className={collection.is_private ? "text-yellow-400" : "text-blue-400"} 
                        />
                        <div className="font-medium text-white truncate">
                          {collection.name}
                        </div>
                        {collection.is_default && (
                          <span className="bg-green-700 text-green-100 px-2 py-0.5 rounded text-xs flex-shrink-0">
                            {t.collections?.defaultCollection || 'По умолчанию'}
                          </span>
                        )}
                      </div>
                      
                      {collection.description && (
                        <p className="text-sm text-gray-300 line-clamp-2">
                          {collection.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {t.common?.cancel || 'Отмена'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            disabled={loading || !hasChanges || selectedCount === 0}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                {t.common?.saving || 'Saving...'}
              </>
            ) : (
              <>
                <Icon name={ICONS.CHECK} size={16} />
                {t.common?.save || 'Save'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CollectionSelectModal;